// JSON-LD builders. Every block is emitted through <SeoHead>, so structured
// data and the visible <head> tags can never drift apart.
//
// Rule: schema must only describe what is actually on the page. Marking up an
// FAQ that isn't rendered, or a rating that doesn't exist, is a structured-data
// violation and a manual-action risk - not a shortcut.

import { SITE, absUrl, PRICE_FROM } from "./siteMeta.js";
import { IRS_EFILE } from "../data/compliance.js";
import { plainText } from "./richText.js";
import { SUPPORT_EMAIL, SUPPORT_PHONE_TEL } from "../components/ui/HelpSupportModal/HelpSupportModal.jsx";

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE.url}/#organization`,
  name: SITE.name,
  alternateName: SITE.alternateNames,
  // Tells Google the brand and the legal entity are one organisation. Directly
  // helps disambiguate us from easy2290.com and easyform2290.com - different
  // companies ranking for the same head term. (The IRS provider list used to
  // show this name; since 2026-09-07 it shows the brand instead.)
  legalName: IRS_EFILE.legalName,
  url: absUrl("/"),
  logo: SITE.logo,
  description: SITE.description,
  // Brand-entity signals (2026-10-22). Each is a plain fact already shown on
  // the site: US-only (Form 2290 is a US federal return), the one subject we
  // file, and the support email and phone printed on /contact.
  areaServed: { "@type": "Country", name: "United States" },
  knowsAbout: ["IRS Form 2290", "Heavy Vehicle Use Tax", "Schedule 1 (Form 2290)"],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: SUPPORT_EMAIL,
    telephone: SUPPORT_PHONE_TEL,
    areaServed: "US",
    availableLanguage: ["English"],
  },
  // Only emitted once a real profile exists - see SITE.sameAs.
  ...(SITE.sameAs.length ? { sameAs: SITE.sameAs } : {}),
});

// The /about page is the brand's own page. AboutPage + `about` pointing at the
// Organization node tells Google this URL is where "who is EasyFile2290" is
// answered.
export const aboutPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: `About ${SITE.name}`,
  url: absUrl("/about"),
  about: { "@id": `${SITE.url}/#organization` },
  isPartOf: { "@id": `${SITE.url}/#website` },
});

export const webSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE.url}/#website`,
  name: SITE.name,
  alternateName: SITE.alternateNames,
  url: absUrl("/"),
  publisher: { "@id": `${SITE.url}/#organization` },
});

// Drives the "how much does it cost" answer in SERPs. Prices mirror the live
// seeded fee schedule (form2290-api ServiceFeeRateSeeder) - if that changes,
// change this. A stale price in schema is worse than no price.
export const softwareApplicationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: SITE.name,
  alternateName: SITE.alternateNames,
  // Joins the product to the brand's Organization node, so the app, the site
  // and the company read as one entity named EasyFile2290.
  publisher: { "@id": `${SITE.url}/#organization` },
  applicationCategory: "FinanceApplication",
  operatingSystem: "Web",
  url: absUrl("/"),
  description: SITE.description,
  offers: {
    "@type": "Offer",
    price: PRICE_FROM,
    priceCurrency: "USD",
    description: "Form 2290 e-filing for a single vehicle",
  },
});

export const breadcrumbSchema = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: absUrl(c.path),
  })),
});

// Answers are authored with the same [label](/path) syntax as body copy (see
// richText.js), so they must be flattened here. JSON-LD has no notion of that
// markup: shipping "[VIN correction](/blog/…)" as an answer string is invalid
// structured data, and it fails silently — the page renders correctly while the
// rich result quietly stops qualifying.
export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: plainText(f.q),
    acceptedAnswer: { "@type": "Answer", text: plainText(f.a) },
  })),
});

// NO `author` AND NO `reviewedBy`, by owner decision (2026-08-14). Article
// structured data has no required properties, so their absence is valid - the
// site is still identified through `publisher`, which points at the
// Organization node.
//
// The reasoning, so nobody "fixes" this later by adding a default back: the
// only author this project could honestly name is the company, and an
// Organization restating the publisher adds nothing a crawler doesn't already
// have. `reviewedBy` needs a real person who really reviewed the content -
// inventing one on tax content is the same class of error as the fabricated
// homepage statistics that were removed rather than reworded (D9). If an EA or
// CPA is ever engaged, both properties come back together with the visible
// byline in BlogPost.jsx, and `author` becomes "@type": "Person".
//
// `image` is an absolute URL to the article's lead image, or undefined when the
// post has no artwork yet. Google treats Article.image as recommended rather
// than required, so emitting the key with a null in it would be worse than
// omitting it - an absent property is silence, a null is a broken value.
// `path` defaults to the English blog URL; Spanish posts live at /es/<slug>.
export const articleSchema = (post, image, { path = `/blog/${post.slug}`, lang = "en" } = {}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: plainText(post.title),
    description: plainText(post.excerpt),
    inLanguage: lang === "es" ? "es-US" : "en-US",
    datePublished: post.publishedAt,
    dateModified: post.updatedAt || post.publishedAt,
    publisher: { "@id": `${SITE.url}/#organization` },
    mainEntityOfPage: absUrl(path),
  };
  if (image) schema.image = image;
  return schema;
};
